'use client'

// Password input component with visibility toggle and strength validation
import { useState, useEffect } from 'react'
import { securityService } from '@/lib/security'

interface PasswordInputProps {
  id: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  showValidation?: boolean
  className?: string
  label?: string
  disabled?: boolean
}

export default function PasswordInput({
  id,
  value,
  onChange,
  placeholder = "Enter password",
  required = false,
  showValidation = true,
  className = "",
  label,
  disabled = false
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [validation, setValidation] = useState<{ isValid: boolean; errors: string[] } | null>(null)

  // Real-time password validation with debounce
  useEffect(() => {
    if (!showValidation || !value) {
      setValidation(null)
      return
    }

    const timeoutId = setTimeout(() => {
      const result = securityService.validatePassword(value)
      setValidation({ isValid: result.valid, errors: result.errors })
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [value, showValidation])

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-stone-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        {/* Lock icon */}
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        
        <input
          type={showPassword ? "text" : "password"}
          id={id}
          value={value}
          onChange={onChange}
          className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 bg-white text-stone-900 placeholder-stone-400 ${
            validation 
              ? validation.isValid 
                ? 'border-emerald-300 focus:ring-emerald-500' 
                : 'border-amber-300 focus:ring-amber-500'
              : 'border-stone-300 focus:ring-emerald-500'
          } ${className}`}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
        />
        
        {/* Password visibility toggle */}
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600 focus:outline-none transition-colors"
          tabIndex={-1}
        >
          {showPassword ? (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>

      {/* Real-time password validation feedback */}
      {showValidation && validation && value && (
        <div className="space-y-2 pt-1">
          {/* Password strength bar */}
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-stone-200 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  validation.isValid 
                    ? 'bg-emerald-500' 
                    : validation.errors.length <= 2 
                      ? 'bg-amber-500' 
                      : 'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.max(20, Math.min(100, 100 - (validation.errors.length * 20)))}%` 
                }}
              />
            </div>
            <span className={`text-xs font-semibold ${
              validation.isValid 
                ? 'text-emerald-600' 
                : validation.errors.length <= 2 
                  ? 'text-amber-600' 
                  : 'text-red-600'
            }`}>
              {validation.isValid 
                ? 'Strong' 
                : validation.errors.length <= 2 
                  ? 'Medium' 
                  : 'Weak'
              }
            </span>
          </div>

          {/* Validation checklist */}
          <div className="text-xs space-y-1">
            {validation.isValid ? (
              <p className="text-emerald-600 flex items-center gap-1">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Password meets all requirements
              </p>
            ) : (
              <div className="space-y-1">
                {validation.errors.map((error, index) => (
                  <p key={index} className="text-stone-500 flex items-center gap-1">
                    <svg className="h-3 w-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
